/**
 * Detect application entry files from repository paths.
 * Mirrors stack_rules.yaml / stack_detection_service entry-point patterns.
 */
const ENTRY_POINT_OPTIONS = [
  'src/server.ts',
  'src/index.ts',
  'src/app.ts',
  'src/main.ts',
  'src/server.js',
  'src/index.js',
  'src/app.js',
  'src/main.js',
  'src/App.tsx',
  'src/App.jsx',
  'src/App.js',
  'src/main.tsx',
  'src/main.jsx',
  'src/index.tsx',
  'src/index.jsx',
  'server.ts',
  'index.ts',
  'app.ts',
  'main.ts',
  'server.js',
  'index.js',
  'app.js',
  'main.js',
  'Program.cs',
  'Startup.cs',
] as const;

const OPTION_RANK = new Map(
  ENTRY_POINT_OPTIONS.map((option, index) => [option, index]),
);

function normalizePath(path: string): string {
  return path.replace(/\\/g, '/').replace(/^\.\//, '');
}

function matchesEntryOption(filePath: string, option: string): boolean {
  const norm = normalizePath(filePath);
  if (norm === option) return true;
  if (norm.endsWith(`/${option}`)) return true;
  const base = norm.split('/').pop() ?? '';
  return base === option;
}

function rankFor(path: string): number {
  const norm = normalizePath(path);
  for (const [option, rank] of OPTION_RANK.entries()) {
    if (matchesEntryOption(norm, option)) return rank;
  }
  return ENTRY_POINT_OPTIONS.length;
}

/** Returns repo-relative entry file paths found in the scan inventory. */
export function findEntryPointFiles(filePaths: string[]): string[] {
  const found = new Set<string>();

  for (const rawPath of filePaths) {
    const norm = normalizePath(rawPath);
    if (!norm) continue;

    for (const option of ENTRY_POINT_OPTIONS) {
      if (matchesEntryOption(norm, option)) {
        found.add(norm);
        break;
      }
    }
  }

  return Array.from(found).sort((a, b) => {
    const rankDiff = rankFor(a) - rankFor(b);
    if (rankDiff !== 0) return rankDiff;
    return a.localeCompare(b);
  });
}

export function formatEntryPoints(filePaths: string[]): string {
  const entries = findEntryPointFiles(filePaths);
  return entries.length > 0 ? entries.join(' · ') : '—';
}
